import { Button } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import Table from "antd/es/table";
import React from "react";
import { useParams } from "react-router-dom";
import { Title } from "../../components/Desc";
import ProgressIndicator from "../../components/ProgressIndicator";
import strings from "../../config/localization";
import {
  useGetResourceTypesQuery,
  useGetTimeslotResourceDetailsQuery,
  useGetTimeslotsQuery,
} from "../../services/appln";
import {
  getLocationKinds,
  getLocationStrMap,
  getReverseLocationStrMap,
} from "../util";
import * as XLSX from "xlsx";

export default function ResourceSummaryReport() {
  const params = useParams();

  const [timeslotMap, setTimeslotMap] = React.useState<any>(
    new Map<string, string>(),
  );
  const [selectedTimeslot, setSelectedTimeslot] = React.useState<any>("");

  const [selectedLocationKinds, setSelectedLocationKinds] = React.useState<any>(
    [strings.area, strings.point, strings.range, strings.subarea],
  );
  const [resourceTypeNames, setResourceTypeNames] = React.useState<any>([]);

  const [showAssessmentCount, setShowAssessmentCount] = React.useState(true);
  const [showAllocationCount, setShowAllocationCount] = React.useState(true);
  const [showAllocationPending, setShowAllocationPending] =
    React.useState(true);

  // selected resource types
  const [selectedResourceTypes, setSelectedResourceTypes] = React.useState<
    Set<string>
  >(new Set<string>());

  const [rawData, setRawData] = React.useState<any>([]);

  interface LocationInfo {
    id: string;
    name: string;
    kind: string;
    details: any;
  }

  const detailsQueryResponse = useGetTimeslotResourceDetailsQuery({
    bandobastId: params.bandobastId,
    timeslotId: timeslotMap.get(selectedTimeslot)!,
  });

  React.useEffect(() => {
    if (!detailsQueryResponse.isSuccess) {
      return;
    }
    setRawData(detailsQueryResponse.data.data);
  }, [detailsQueryResponse]);

  const timeslotsQueryResponse = useGetTimeslotsQuery(params.bandobastId);
  React.useEffect(() => {
    if (!timeslotsQueryResponse.isSuccess) {
      return;
    }
    const timeslotMap = new Map<string, string>();
    timeslotsQueryResponse.data?.data.forEach((element: any) => {
      timeslotMap.set(element.name, element.id);
    });
    setTimeslotMap(timeslotMap);
  }, [timeslotsQueryResponse]);

  const resourceTypesQueryResponse = useGetResourceTypesQuery({
    page: 1,
    limit: 1000,
    search: "",
  });
  React.useEffect(() => {
    if (!resourceTypesQueryResponse.isSuccess) {
      return;
    }
    const resourceTypeNames = resourceTypesQueryResponse.data.data
      .map((e: any) => e.name)
      .sort();
    setResourceTypeNames(resourceTypeNames);
    setSelectedResourceTypes(new Set<string>(resourceTypeNames));
  }, [resourceTypesQueryResponse]);

  if (
    detailsQueryResponse.isLoading ||
    detailsQueryResponse.isFetching ||
    timeslotsQueryResponse.isLoading ||
    timeslotsQueryResponse.isFetching ||
    resourceTypesQueryResponse.isLoading ||
    resourceTypesQueryResponse.isFetching
  ) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          width: "100%",
          marginTop: "10px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <Title text={strings.resourceAssessmentAllocationSummary}></Title>
        <ProgressIndicator />
      </Box>
    );
  }

  // console.log("SummaryReport, selectedTimeslot", selectedTimeslot);
  // console.log("SummaryReport, selectedLocationKinds", selectedLocationKinds);
  console.log("SummaryReport, rawData", rawData);

  const locationMap = getReverseLocationStrMap();
  const locationKindsUpdated = selectedLocationKinds.map((e: string) => {
    return locationMap.get(e);
  });
  // console.log("SummaryReport, locationKindsUpdated", locationKindsUpdated);

  const rows = rawData.filter((e: any) => {
    return locationKindsUpdated.indexOf(e.kind) > -1;
  });
  // console.log("SummaryReport, rows", rows);

  const columns: ColumnsType<LocationInfo> = [
    // uncomment following to debug sequence related issues.
    // {
    //   title: strings.serialNo,
    //   dataIndex: "serialNo",
    //   key: "serialNo",
    // },
    {
      title: strings.name,
      dataIndex: "name",
      key: "name",
    },
    {
      title: strings.kind,
      dataIndex: "kind",
      key: "kind",
      render: (text, record, index) => getLocationStrMap().get(text)!,
    },
  ];

  resourceTypeNames.forEach((name: string) => {
    // add resource type column only if its selected
    if (selectedResourceTypes.has(name)) {
      columns.push({
        title: name,
        dataIndex: name,
        key: name,
        render: (text, record, index) => {
          console.log("XX: text, record, index", text, record, index);
          const row: any = record["details"].find((e: any) => {
            return name === e.resourceTypeName;
          });
          console.log("XX: row", row);
          console.log("XX: name", name);
          return (
            <Space direction="horizontal">
              {showAssessmentCount && (
                <Tag color="magenta">{row["assessmentCount"]}</Tag>
              )}
              {showAllocationCount && (
                <Tag color="cyan">{row["allocationCount"]}</Tag>
              )}
              {showAllocationPending && (
                <Tag color="blue">{row["allocationPendingCount"]}</Tag>
              )}
            </Space>
          );
        },
      });
    }
  });

  return (
    <div>
      <Box
        sx={{
          height: 400,
          width: "100%",
          marginTop: "10px",
          marginLeft: "20px",
          marginRight: "20px",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Title text={strings.resourceAssessmentAllocationSummary}></Title>
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              options={Array.from(timeslotMap.keys())}
              value={selectedTimeslot}
              onChange={(e, value) => {
                setSelectedTimeslot(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="standard"
                  label={strings.timeslot}
                />
              )}
            />
          </Grid>
          <Grid item xs={7}>
            <Autocomplete
              multiple
              options={getLocationKinds()}
              value={selectedLocationKinds}
              onChange={(e, value) => {
                console.log("selected location kinds", value);
                setSelectedLocationKinds(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="normal"
                  variant="standard"
                  label={strings.kind}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <FormGroup row={true}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAssessmentCount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAssessmentCount(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAssessmentCount}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllocationCount}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAllocationCount(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAllocationCount}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showAllocationPending}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setShowAllocationPending(event.target.checked);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                label={strings.showAllocationPending}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row={true}>
              {resourceTypeNames.map((name: string) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedResourceTypes.has(name)}
                      color="secondary"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        // console.log(
                        //   "name:",
                        //   name,
                        //   "event.target.checked:",
                        //   event.target.checked,
                        // );
                        if (event.target.checked) {
                          selectedResourceTypes.add(name);
                        } else {
                          selectedResourceTypes.delete(name);
                        }
                        // create a copy since updating existing copy does not refresh the ui
                        const newSelectedDesignations = new Set<string>([
                          ...selectedResourceTypes,
                        ]);
                        // console.log("newSelectedDesignations:", [
                        //   ...newSelectedDesignations,
                        // ]);
                        setSelectedResourceTypes(newSelectedDesignations);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={name}
                />
              ))}
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={() => {
                console.log("in export handler");
                // console.log("excel sheet rows", rows);
                const assessmentTotalsCount = new Map<string, number>();
                const allocationTotalsCount = new Map<string, number>();
                resourceTypeNames.forEach((designationName: string) => {
                  assessmentTotalsCount.set(designationName, 0);
                  allocationTotalsCount.set(designationName, 0);
                });
                const excelData = rows.map((row: any) => {
                  const output: any = {};
                  output[strings.name] = row.name;
                  output[strings.kind] = getLocationStrMap().get(row.kind)!;
                  const details = row.details;
                  details.forEach((entry: any) => {
                    const designationName = entry["designationName"];
                    if (selectedResourceTypes.has(designationName)) {
                      if (showAssessmentCount) {
                        output[`${designationName} - Assessment count`] =
                          entry["assessmentCount"];
                      }
                      if (showAllocationCount) {
                        output[`${designationName} - Allocated count`] =
                          entry["allocationCount"];
                      }
                      if (showAllocationPending) {
                        output[`${designationName} - Allocated pending count`] =
                          entry["allocationPendingCount"];
                      }

                      assessmentTotalsCount.set(
                        designationName,
                        assessmentTotalsCount.get(designationName)! +
                          entry["assessmentCount"],
                      );
                      allocationTotalsCount.set(
                        designationName,
                        allocationTotalsCount.get(designationName)! +
                          entry["allocationCount"],
                      );
                    }
                  });
                  return output;
                });
                const totalRow: any = {};
                totalRow[strings.name] = strings.total;
                totalRow[strings.kind] = "";
                resourceTypeNames.forEach((name: string) => {
                  if (selectedResourceTypes.has(name)) {
                    if (showAssessmentCount) {
                      totalRow[`${name} - Assessment count`] =
                        assessmentTotalsCount.get(name)!;
                    }
                    if (showAllocationCount) {
                      totalRow[`${name} - Allocated count`] =
                        allocationTotalsCount.get(name)!;
                    }
                    if (showAllocationPending) {
                      totalRow[`${name} - Allocated pending count`] =
                        assessmentTotalsCount.get(name)! -
                        allocationTotalsCount.get(name)!;
                    }
                  }
                });
                excelData.push(totalRow);
                const worksheet = XLSX.utils.json_to_sheet(excelData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(
                  workbook,
                  worksheet,
                  selectedTimeslot,
                );
                XLSX.writeFile(workbook, "report.xlsx");
              }}
            >
              {strings.export}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Table
              scroll={{ y: 300 }}
              columns={columns}
              dataSource={rows}
              pagination={false}
              summary={(pageData) => {
                console.log("pageData", pageData);
                const assessmentTotalsCount = new Map<string, number>();
                const allocationTotalsCount = new Map<string, number>();
                resourceTypeNames.forEach((designationName: string) => {
                  assessmentTotalsCount.set(designationName, 0);
                  allocationTotalsCount.set(designationName, 0);
                });
                pageData.forEach((e: any) => {
                  const details = e["details"];
                  details.forEach((entry: any) => {
                    const resourceTypeName = entry["resourceTypeName"];
                    assessmentTotalsCount.set(
                      resourceTypeName,
                      assessmentTotalsCount.get(resourceTypeName)! +
                        entry["assessmentCount"],
                    );
                    allocationTotalsCount.set(
                      resourceTypeName,
                      allocationTotalsCount.get(resourceTypeName)! +
                        entry["allocationCount"],
                    );
                  });
                });

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <b>{strings.total}</b>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={0}></Table.Summary.Cell>
                      {resourceTypeNames.map(
                        (resourceTypeName: string, index: number) => {
                          return (
                            <>
                              <Table.Summary.Cell index={index + 1}>
                                <Space direction="horizontal">
                                  {showAssessmentCount && (
                                    <Tag color="magenta">
                                      {
                                        assessmentTotalsCount.get(
                                          resourceTypeName,
                                        )!
                                      }
                                    </Tag>
                                  )}
                                  {showAllocationCount && (
                                    <Tag color="cyan">
                                      {
                                        allocationTotalsCount.get(
                                          resourceTypeName,
                                        )!
                                      }
                                    </Tag>
                                  )}
                                  {showAllocationPending && (
                                    <Tag color="blue">
                                      {assessmentTotalsCount.get(
                                        resourceTypeName,
                                      )! -
                                        allocationTotalsCount.get(
                                          resourceTypeName,
                                        )!}
                                    </Tag>
                                  )}
                                </Space>
                              </Table.Summary.Cell>
                            </>
                          );
                        },
                      )}
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
